import { Injectable } from '@angular/core';
import { AgGridFactory, AgGridNoRowsOverlayComponent, AgGridZeroStateMessages, LoadableStatus, TEXT_COLUMN } from '@anduin/shared';
import { GridOptions, GridReadyEvent } from 'ag-grid-community';
import { FailedEmailRow } from '../../../models';

interface FailedGridFactoryParams<Data = unknown> {
  onGridReadyCallback: (event: GridReadyEvent<Data>) => void;
  getGridDataStatus: () => LoadableStatus;
  isExternalFilterPresent: () => boolean;
}

@Injectable()
export class FailedGridOptionsFactory {
  constructor(private readonly gridFactory: AgGridFactory) {}

  create(params: FailedGridFactoryParams): GridOptions<FailedEmailRow> {
    const baseOptions = this.gridFactory.serverSideGridOption({
      zeroStateMessages: getZeroStateMessages(),
      getGridDataStatus: () => params.getGridDataStatus(),
    });

    return {
      ...baseOptions,
      isExternalFilterPresent: () => params.isExternalFilterPresent(),
      serverSideInfiniteScroll: true,
      defaultColDef: {
        ...TEXT_COLUMN(),
        sortable: true,
        resizable: true,
        minWidth: 100,
      },
      getRowId: (p) => String(p.data.id),
      suppressRowClickSelection: true,
      suppressNoRowsOverlay: false,
      noRowsOverlayComponent: AgGridNoRowsOverlayComponent,
      noRowsOverlayComponentParams: {
        headerRowsCount: 2,
        asyncMessage: baseOptions.context?.zeroStateOverlayMessage$,
      },
      onGridReady: params.onGridReadyCallback,
    };
  }

  createExportGridOptions(): GridOptions {
    return {
      ...this.gridFactory.serverSideGridOption(),
      defaultColDef: {
        ...TEXT_COLUMN(),
        sortable: true,
        resizable: true,
        minWidth: 100,
      },
      suppressRowClickSelection: true,
    };
  }
}

function getZeroStateMessages(): AgGridZeroStateMessages {
  const getMessage = (header: string, body: string) => `
    <div class="d-flex flex-column align-items-center justify-content-center p-4 max-width-420">
      <h3 class="mb-2">${header}</h3>
      <p class="tc--gray-400">${body}</p>
    </div>
  `;

  return {
    empty: getMessage('No failed emails', 'There are currently no failed email records.'),
    error: getMessage('An error occurred', 'There was an error retrieving records. Please try again.'),
    filter: getMessage('No failed emails', 'No failed emails match your selected criteria. Try adjusting the filters above.'),
  };
}
